import { fromMarkdown } from 'mdast-util-from-markdown';
import { toMarkdown } from 'mdast-util-to-markdown';

function addTextToDraftRaw(prevDraftRaw, text) {
  return {
    ...prevDraftRaw,
    blocks: [
      {
        ...prevDraftRaw.blocks[0],
        text: `${prevDraftRaw.blocks[0].text}${text}`,
      },
    ],
  };
}

function addPhrasingContentToDraftRaw(draftRaw, nodes, level = 0) {
  return nodes.reduce((prevDraftRaw, node) => {
    const offsetHere = prevDraftRaw.blocks[0].text.length;
    switch (node.type) {
      case 'text':
      case 'inlineCode':
        return addTextToDraftRaw(prevDraftRaw, node.value.replace('\n', ' '));
      case 'break':
        return addTextToDraftRaw(' ');
      case 'html':
        switch (node.value) {
          case '<sup>':
            return {
              ...prevDraftRaw,
              inHtmlSpan: {
                tag: 'sup',
                startOffset: offsetHere,
                level,
              },
            };
          case '</sup>':
            if (
              prevDraftRaw.inHtmlSpan &&
              prevDraftRaw.inHtmlSpan.tag === 'sup' &&
              prevDraftRaw.inHtmlSpan.level === level
            ) {
              return {
                ...prevDraftRaw,
                inHtmlSpan: null,
                blocks: [
                  {
                    ...prevDraftRaw.blocks[0],
                    inlineStyleRanges: [
                      ...prevDraftRaw.blocks[0].inlineStyleRanges,
                      {
                        style: 'SUPERSCRIPT',
                        offset: prevDraftRaw.inHtmlSpan.startOffset,
                        length:
                          offsetHere - prevDraftRaw.inHtmlSpan.startOffset,
                      },
                    ],
                  },
                ],
              };
            }
            break;
          case '<sub>':
            return {
              ...prevDraftRaw,
              inHtmlSpan: {
                tag: 'sub',
                startOffset: offsetHere,
                level,
              },
            };
          case '</sub>':
            if (
              prevDraftRaw.inHtmlSpan &&
              prevDraftRaw.inHtmlSpan.tag === 'sub' &&
              prevDraftRaw.inHtmlSpan.level === level
            ) {
              return {
                ...prevDraftRaw,
                inHtmlSpan: null,
                blocks: [
                  {
                    ...prevDraftRaw.blocks[0],
                    inlineStyleRanges: [
                      ...prevDraftRaw.blocks[0].inlineStyleRanges,
                      {
                        style: 'SUBSCRIPT',
                        offset: prevDraftRaw.inHtmlSpan.startOffset,
                        length:
                          offsetHere - prevDraftRaw.inHtmlSpan.startOffset,
                      },
                    ],
                  },
                ],
              };
            }
            break;
        }
        return prevDraftRaw;
      case 'emphasis': {
        const withContainedContent = addPhrasingContentToDraftRaw(
          prevDraftRaw,
          node.children,
          level + 1,
        );
        return {
          ...withContainedContent,
          blocks: [
            {
              ...withContainedContent.blocks[0],
              inlineStyleRanges: [
                ...withContainedContent.blocks[0].inlineStyleRanges,
                {
                  style: 'ITALIC',
                  offset: offsetHere,
                  length:
                    withContainedContent.blocks[0].text.length - offsetHere,
                },
              ],
            },
          ],
        };
      }
      case 'strong':
      case 'linkReference':
        // Ignore these spans, but add whatever’s inside them
        return addPhrasingContentToDraftRaw(
          prevDraftRaw,
          node.children,
          level + 1,
        );
      case 'link': {
        // Special case handling for defined terms – if the
        // original text has been preserved, then use that
        // instead of the link text
        if (node.url === '#defs' && node.title) {
          return addTextToDraftRaw(prevDraftRaw, node.title);
        }
        // Standard link handling is to add whatever’s inside
        // (the link will get re-applied later if it still matches)
        return addPhrasingContentToDraftRaw(
          prevDraftRaw,
          node.children,
          level + 1,
        );
      }
      case 'image':
      case 'imageReference':
      default:
        // Images, and anything unexpected, are ignored
        return prevDraftRaw;
    }
  }, draftRaw);
}

const stripEditingDataFromLinks = (ast) => ({
  ...ast,
  ...(ast.type === 'link'
    ? {
        title: undefined,
        url: undefined,
      }
    : {}),
  ...(ast.children
    ? {
        children: ast.children.map((child) => stripEditingDataFromLinks(child)),
      }
    : {}),
});

export function markdownToDraftRaw(markdownText) {
  if (!markdownText) {
    return null;
  }

  let ast = null;
  try {
    ast = fromMarkdown(markdownText);
  } catch (error) {
    console.error(`Error parsing Markdown: %{error}`);
    return null;
  }
  if (ast?.type !== 'root' || !Array.isArray(ast?.children)) {
    return null;
  }

  for (const topLevelBlock of ast.children) {
    if (topLevelBlock?.type === 'paragraph') {
      const draftRaw = addPhrasingContentToDraftRaw(
        {
          blocks: [
            {
              text: '',
              inlineStyleRanges: [],
            },
          ],
          entityMap: {},
        },
        topLevelBlock.children,
      );
      // console.debug({ markdownToDraftRaw: draftRaw, markdownText });
      return draftRaw;
    }
  }
  return null;
}

const matchMarkdownWhitespace = /\s/;
const stripWhitespace = /(^\s*)(.*?)(\s*$)/s;
const matchMarkdownPunctuation =
  /[!-/:-@[-`{-~\xA1\xA7\xAB\xB6\xB7\xBB\xBF\u037E\u0387\u055A-\u055F\u0589\u058A\u05BE\u05C0\u05C3\u05C6\u05F3\u05F4\u0609\u060A\u060C\u060D\u061B\u061E\u061F\u066A-\u066D\u06D4\u0700-\u070D\u07F7-\u07F9\u0830-\u083E\u085E\u0964\u0965\u0970\u09FD\u0A76\u0AF0\u0C77\u0C84\u0DF4\u0E4F\u0E5A\u0E5B\u0F04-\u0F12\u0F14\u0F3A-\u0F3D\u0F85\u0FD0-\u0FD4\u0FD9\u0FDA\u104A-\u104F\u10FB\u1360-\u1368\u1400\u166E\u169B\u169C\u16EB-\u16ED\u1735\u1736\u17D4-\u17D6\u17D8-\u17DA\u1800-\u180A\u1944\u1945\u1A1E\u1A1F\u1AA0-\u1AA6\u1AA8-\u1AAD\u1B5A-\u1B60\u1BFC-\u1BFF\u1C3B-\u1C3F\u1C7E\u1C7F\u1CC0-\u1CC7\u1CD3\u2010-\u2027\u2030-\u2043\u2045-\u2051\u2053-\u205E\u207D\u207E\u208D\u208E\u2308-\u230B\u2329\u232A\u2768-\u2775\u27C5\u27C6\u27E6-\u27EF\u2983-\u2998\u29D8-\u29DB\u29FC\u29FD\u2CF9-\u2CFC\u2CFE\u2CFF\u2D70\u2E00-\u2E2E\u2E30-\u2E4F\u2E52\u3001-\u3003\u3008-\u3011\u3014-\u301F\u3030\u303D\u30A0\u30FB\uA4FE\uA4FF\uA60D-\uA60F\uA673\uA67E\uA6F2-\uA6F7\uA874-\uA877\uA8CE\uA8CF\uA8F8-\uA8FA\uA8FC\uA92E\uA92F\uA95F\uA9C1-\uA9CD\uA9DE\uA9DF\uAA5C-\uAA5F\uAADE\uAADF\uAAF0\uAAF1\uABEB\uFD3E\uFD3F\uFE10-\uFE19\uFE30-\uFE52\uFE54-\uFE61\uFE63\uFE68\uFE6A\uFE6B\uFF01-\uFF03\uFF05-\uFF0A\uFF0C-\uFF0F\uFF1A\uFF1B\uFF1F\uFF20\uFF3B-\uFF3D\uFF3F\uFF5B\uFF5D\uFF5F-\uFF65]/;

export function stripLeadingAndTrailingWhitespace(text) {
  const splits = text.match(stripWhitespace);
  return {
    leadingCharactersStripped: splits?.[1]?.length || 0,
    text: splits?.[2] || '',
    trailingCharactersStripped: splits?.[3]?.length || 0,
  };
}

function classifyCharForDelimiterRun(char) {
  if (matchMarkdownWhitespace.test(char)) {
    return 'whitespace';
  }
  if (matchMarkdownPunctuation.test(char)) {
    return 'punctuation';
  }
  return 'word';
}

function applyRangeToMarkdownAstNode(node, range) {
  const newNode = {
    ...node,
    children: [],
  };
  for (const child of node.children) {
    if (
      child.textPosition.end <= range.offsetStart ||
      child.textPosition.start >= range.offsetEnd
    ) {
      // no overlap, so copy child across unmodified
      newNode.children.push(child);
      continue;
    }
    switch (child.type) {
      case 'link':
      case 'linkReference':
        // For now, don’t allow any styling to be applied inside a link
        newNode.children.push(child);
        break;
      case 'emphasis':
      case 'strong':
        // recurse to apply the range to children
        newNode.children.push(applyRangeToMarkdownAstNode(child, range));
        break;
      case 'text': {
        // calculate the new text node for the portion of the range
        // being applied that overlaps with this child
        let leftTrim = 0;
        let rightTrim = 0;
        const overlapTextNode = {
          type: 'text',
          value: child.value.slice(
            Math.max(0, range.offsetStart - child.textPosition.start),
            range.offsetEnd - child.textPosition.start,
          ),
          textPosition: {
            start: Math.max(child.textPosition.start, range.offsetStart),
            end: Math.min(child.textPosition.end, range.offsetEnd),
          },
        };
        if (range.type === 'LINK' && range.data.url === '#defs') {
          // For defined terms, replace the original text with the term as defined
          overlapTextNode.textPosition.originalText = overlapTextNode.value;
          overlapTextNode.value = range.data.definedTerm;
        }

        // When applying an italic range, shrink the overlapping
        // range to a position in the text where Markdown emphasis
        // markers will be correctly interpreted
        if (range.type === 'ITALIC') {
          let prevCharClass = null;
          for (const leftChar of overlapTextNode.value) {
            const charClass = classifyCharForDelimiterRun(leftChar);
            if (charClass === 'word') {
              // emphasis '*' can be inserted here
              break;
            }
            if (charClass === 'punctuation') {
              if (
                prevCharClass === 'whitespace' ||
                prevCharClass === 'punctuation'
              ) {
                // emphasis '*' can be inserted here
                break;
              }
            }
            leftTrim += 1;
            prevCharClass = charClass;
          }
          prevCharClass = null;
          for (
            let index = overlapTextNode.value.length - 1;
            index >= leftTrim;
            index -= 1
          ) {
            const rightChar = overlapTextNode.value[index];
            const charClass = classifyCharForDelimiterRun(rightChar);
            if (charClass === 'word') {
              // emphasis '*' can be inserted here
              break;
            }
            if (charClass === 'punctuation') {
              if (
                prevCharClass === 'whitespace' ||
                prevCharClass === 'punctuation'
              ) {
                // emphasis '*' can be inserted here
                break;
              }
            }
            rightTrim += 1;
            prevCharClass = charClass;
          }
          if (leftTrim + rightTrim >= overlapTextNode.value.length) {
            // Nothing left, so don’t modify
            newNode.children.push(child);
            break;
          }
          // Apply any necessary trim to the emphasized range
          overlapTextNode.value = overlapTextNode.value.slice(
            leftTrim,
            overlapTextNode.value.length - rightTrim,
          );
          overlapTextNode.textPosition.start += leftTrim;
          overlapTextNode.textPosition.end -= rightTrim;
        }

        // If necessary, split off a new text node for the
        // portion before the applied range
        if (overlapTextNode.textPosition.start > child.textPosition.start) {
          newNode.children.push({
            type: 'text',
            value: child.value.slice(
              0,
              overlapTextNode.textPosition.start - child.textPosition.start,
            ),
            textPosition: {
              start: child.textPosition.start,
              end: overlapTextNode.textPosition.start,
            },
          });
        }

        // Add the new node applying the range to the overlapping
        // portion of text
        switch (range.type) {
          case 'LINK': {
            const linkNode = {
              ...range.data,
              type: 'link',
              children: [overlapTextNode],
              textPosition: overlapTextNode.textPosition,
            };
            if (overlapTextNode.textPosition.originalText) {
              // If the link text has been substituted, preserve the original
              // text in the link title
              linkNode.title = overlapTextNode.textPosition.originalText;
            }
            newNode.children.push(linkNode);
            break;
          }
          case 'SUPERSCRIPT':
            newNode.children.push({
              type: 'html',
              value: '<sup>',
              textPosition: {
                start: overlapTextNode.textPosition.start,
                end: overlapTextNode.textPosition.start,
              },
            });
            newNode.children.push(overlapTextNode);
            newNode.children.push({
              type: 'html',
              value: '</sup>',
              textPosition: {
                start: overlapTextNode.textPosition.end,
                end: overlapTextNode.textPosition.end,
              },
            });
            break;
          case 'SUBSCRIPT':
            newNode.children.push({
              type: 'html',
              value: '<sub>',
              textPosition: {
                start: overlapTextNode.textPosition.start,
                end: overlapTextNode.textPosition.start,
              },
            });
            newNode.children.push(overlapTextNode);
            newNode.children.push({
              type: 'html',
              value: '</sub>',
              textPosition: {
                start: overlapTextNode.textPosition.end,
                end: overlapTextNode.textPosition.end,
              },
            });
            break;
          case 'ITALIC':
            newNode.children.push({
              type: 'emphasis',
              children: [overlapTextNode],
              textPosition: overlapTextNode.textPosition,
            });
            break;
        }

        // If necessary, split off a new text node for the
        // portion after the applied range
        if (overlapTextNode.textPosition.end < child.textPosition.end) {
          newNode.children.push({
            type: 'text',
            value: child.value.slice(
              overlapTextNode.textPosition.end - child.textPosition.start,
            ),
            textPosition: {
              start: overlapTextNode.textPosition.end,
              end: child.textPosition.end,
            },
          });
        }
        break;
      }
      default:
        // ranges have no effect on other existing nodes, like html tags
        newNode.children.push(child);
        break;
    }
  }
  return newNode;
}

// exampleLinkOverrides = [
//   {
//     offset: 17,
//     length: 8,
//     definedTerm: 'The Term',
//   },
// ];
export function draftRawToMarkdown(draftRaw, linkOverrides, cleanForPosting) {
  const block = draftRaw?.blocks?.[0];
  if (!block) {
    return null;
  }

  const strippedText = stripLeadingAndTrailingWhitespace(block.text);
  const markdownAst = {
    type: 'root',
    children: [
      {
        type: 'paragraph',
        children: [
          {
            type: 'text',
            value: strippedText.text,
            textPosition: {
              start: 0,
              end: strippedText.text.length,
            },
          },
        ],
        textPosition: {
          start: 0,
          end: strippedText.text.length,
        },
      },
    ],
  };

  let links;
  if (linkOverrides) {
    links = linkOverrides.map(({ offset, length, definedTerm }) => ({
      type: 'LINK',
      offsetStart: offset - strippedText.leadingCharactersStripped,
      offsetEnd: offset - strippedText.leadingCharactersStripped + length,
      data: {
        url: '#defs',
        definedTerm,
      },
    }));
  } else {
    const entityRanges = block.entityRanges || [];
    links = entityRanges
      .filter(
        (entityRange) => draftRaw.entityMap[entityRange.key]?.type === 'LINK',
      )
      .map((entityRange) => ({
        type: 'LINK',
        offsetStart:
          entityRange.offset - strippedText.leadingCharactersStripped,
        offsetEnd:
          entityRange.offset -
          strippedText.leadingCharactersStripped +
          entityRange.length,
        data: draftRaw.entityMap[entityRange.key]?.data,
      }));
  }
  links.forEach((range) => {
    markdownAst.children[0] = applyRangeToMarkdownAstNode(
      markdownAst.children[0],
      range,
    );
  });

  const styleRanges = block.inlineStyleRanges || [];
  styleRanges
    .filter(
      (styleRange) =>
        styleRange.style === 'SUPERSCRIPT' || styleRange.style === 'SUBSCRIPT',
    )
    .map((styleRange) => ({
      type: styleRange.style,
      offsetStart: styleRange.offset - strippedText.leadingCharactersStripped,
      offsetEnd:
        styleRange.offset -
        strippedText.leadingCharactersStripped +
        styleRange.length,
    }))
    .forEach((range) => {
      markdownAst.children[0] = applyRangeToMarkdownAstNode(
        markdownAst.children[0],
        range,
      );
    });

  styleRanges
    .filter((styleRange) => styleRange.style === 'ITALIC')
    .map((styleRange) => ({
      type: styleRange.style,
      offsetStart: styleRange.offset - strippedText.leadingCharactersStripped,
      offsetEnd:
        styleRange.offset -
        strippedText.leadingCharactersStripped +
        styleRange.length,
    }))
    .forEach((range) => {
      markdownAst.children[0] = applyRangeToMarkdownAstNode(
        markdownAst.children[0],
        range,
      );
    });

  let markdown = null;
  try {
    markdown = toMarkdown(
      cleanForPosting ? stripEditingDataFromLinks(markdownAst) : markdownAst,
    );
  } catch (error) {
    console.error(`Error creating Markdown: %{error}`);
    return null;
  }
  if (typeof markdown === 'string') {
    // The compiler puts a newline at the end of the paragraph, which we don’t need
    markdown = stripLeadingAndTrailingWhitespace(markdown).text;
  }
  // console.debug({ draftRawToMarkdown: markdown, draftRaw, cleanForPosting });
  return markdown;
}
